import { buildTutorialTargetMissingHint } from '../buildTargetMissingHint';
import type { TutorialDefinition, TutorialRecover } from '../types';

const R = {
  annual: {
    path: '/annual-progress-term1',
    pageLabel: '연간진도표',
    openSelectors: [
      'a[href^="/annual-progress-term1"]',
      'a[href^="/annual-progress-term2"]',
      '.cp-sidebar-annual-progress-link',
    ],
    openLabel: '연간진도표',
    accessLabel: '사이드바 「교육과정 노트」',
  },
  editor: {
    path: '/annual-progress-term1',
    openSelector: '[data-tutorial-ws="open-worksheet"]',
    pageLabel: '연간진도표',
    openLabel: '활동지',
  },
} as const satisfies Record<string, TutorialRecover>;

const RECOVER_BY_STEP_ID: Record<string, TutorialRecover> = {
  'go-annual-progress': R.annual,
  'select-cell': R.annual,
  'open-worksheet': R.annual,
  'ws-overview': R.editor,
  'ws-tool-text': R.editor,
  'ws-tool-shape': R.editor,
  'ws-tool-link-image': R.editor,
  'ws-tool-clipboard-image': R.editor,
  'ws-tool-scan-image': R.editor,
  'ws-tool-table': R.editor,
  'ws-tool-undo': R.editor,
  'ws-tool-redo': R.editor,
  'ws-tool-snap': R.editor,
  'ws-tool-front': R.editor,
  'ws-tool-back': R.editor,
  'ws-tool-align': R.editor,
  'ws-force-add-text': R.editor,
  'ws-force-add-page': R.editor,
  'ws-practice': R.editor,
};

const classWorksheetRaw: TutorialDefinition = {
  id: 'class-worksheet',
  title: '수업 활동지',
  summary:
    '연간진도표에서 활동지를 연 뒤, 개체 블럭·페이지 도구를 익히고 캔버스에서 실습해 봐요.',
  steps: [
    {
      id: 'intro',
      mascot:
        '수업 활동지 튜토리얼을 시작해요! 연간진도표에서 활동지를 열고, 도구를 하나씩 살펴본 뒤 직접 추가해 볼게요.',
      actionLabel: '시작하기',
      presentation: 'intro',
      cardTitle: '수업 활동지를 시작해요!',
      placement: 'center',
    },
    {
      id: 'go-annual-progress',
      mascot:
        '사이드바에서 「1학기 연간진도표」또는 「2학기 연간진도표」를 눌러 이동해 주세요. 프로그램 표가 있는 화면이면 됩니다.',
      targetSelector:
        'a[href^="/annual-progress-term1"], a[href^="/annual-progress-term2"], .cp-sidebar-annual-progress-link',
      placement: 'right',
      advanceOnPathPrefix: ['/annual-progress-term1', '/annual-progress-term2', '/program/'],
      section: '[활동지] 페이지 열기',
      sectionMascot: '연간진도·프로그램 표로 이동하는 구간이에요.',
    },
    {
      id: 'select-cell',
      mascot:
        '표에서 학습내용이 있는 칸(또는 행)을 하나 선택한 뒤 "다음"을 눌러 주세요. 선택한 행의 활동지를 열어요.',
      targetSelector:
        '.cp-annual-progress-table, .cp-program-progress-table, .cp-annual-progress-grid',
      manualAdvance: true,
      section: '[활동지] 행 선택 · 열기',
      sectionMascot: '활동지를 열 수업을 고르는 구간이에요.',
    },
    {
      id: 'open-worksheet',
      mascot: '상단 「활동지」버튼을 눌러 활동지 편집 창을 열어 주세요.',
      targetSelector: '[data-tutorial-ws="open-worksheet"]',
      advanceWhenSelectorExists: '[data-tutorial-ws="editor-modal"]',
    },
    {
      id: 'ws-overview',
      mascot:
        '활동지 A4 캔버스예요. 왼쪽·오른쪽 도구를 하나씩 살펴본 뒤, 마지막에 자유롭게 작성해 볼게요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ws="editor-modal"]',
      manualAdvance: true,
      blockTargetClick: true,
      section: '[활동지] 도구 둘러보기',
      sectionMascot: '개체 블럭과 편집 도구를 알아보는 구간이에요.',
    },
    {
      id: 'ws-tool-text',
      mascot:
        '왼쪽 「텍스트 블럭」은 글상자를 캔버스에 추가해요. 제목·안내 문구에 씁니다. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ws="toolbar-left"] [aria-label="텍스트 블럭 추가"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'ws-tool-shape',
      mascot:
        '「도형 블럭」메뉴에서는 직사각형·원형·직선 도형을 넣을 수 있어요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ws="toolbar-left"] [aria-label="도형 블럭 추가 메뉴"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'ws-tool-link-image',
      mascot: '「링크 이미지」는 URL로 이미지를 캔버스에 넣어요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ws="toolbar-left"] [aria-label="링크 이미지 블럭 추가"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'ws-tool-clipboard-image',
      mascot:
        '「클립보드 이미지」는 복사한 그림을 붙여넣어 넣어요(Ctrl+V). "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-ws="toolbar-left"] [aria-label="클립보드 이미지 블럭 추가 (Ctrl+V로 붙여넣기)"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'ws-tool-scan-image',
      mascot: '「스캔 이미지」는 웹캠·스캔으로 가져온 이미지를 넣어요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ws="toolbar-left"] [aria-label="스캔 이미지 블럭 추가"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'ws-tool-table',
      mascot:
        '「표 블럭」은 행·열 표를 캔버스에 추가해요. 문항표·채점표에 자주 씁니다. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ws="toolbar-left"] [aria-label="표 블럭 추가"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'ws-tool-undo',
      mascot: '오른쪽 「되돌리기」는 바로 전 편집을 취소해요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ws="toolbar-right"] [aria-label="편집 되돌리기"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'ws-tool-redo',
      mascot: '「다시 실행」은 되돌리기를 다시 적용해요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ws="toolbar-right"] [aria-label="편집 다시 실행"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'ws-tool-snap',
      mascot:
        '「스마트 스냅」은 개체를 움직일 때 정렬선을 도와주는 기능이에요. "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-ws="toolbar-right"] [aria-label="스마트 스냅 끄기"], [data-tutorial-ws="toolbar-right"] [aria-label="스마트 스냅 켜기"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'ws-tool-front',
      mascot: '「맨앞으로 보내기」는 선택한 개체를 가장 위로 올려요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ws="toolbar-right"] [aria-label="개체 맨앞으로 보내기"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'ws-tool-back',
      mascot: '「맨뒤로 보내기」는 선택한 개체를 가장 아래로 내려요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-ws="toolbar-right"] [aria-label="개체 맨뒤로 보내기"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'ws-tool-align',
      mascot:
        '정렬 도구로 왼쪽·중앙·오른쪽·위·아래 정렬과 가로·세로 등간격 정렬을 할 수 있어요. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-ws="toolbar-right"] [aria-label="왼쪽정렬"], [data-tutorial-ws="toolbar-right"] [aria-label="중앙정렬"], [data-tutorial-ws="toolbar-right"] [aria-label="오른쪽정렬"], [data-tutorial-ws="toolbar-right"] [aria-label="위쪽정렬"], [data-tutorial-ws="toolbar-right"] [aria-label="상하중앙정렬"], [data-tutorial-ws="toolbar-right"] [aria-label="아래정렬"], [data-tutorial-ws="toolbar-right"] [aria-label="가로 등간격 정렬"], [data-tutorial-ws="toolbar-right"] [aria-label="세로 등간격 정렬"]',
      manualAdvance: true,
      blockTargetClick: true,
      spotlightSeparate: true,
    },
    {
      id: 'ws-force-add-text',
      mascot: '이제 「텍스트 블럭」을 눌러 캔버스에 글상자를 직접 추가해 보세요.',
      targetSelector: '[data-tutorial-ws="toolbar-left"] [aria-label="텍스트 블럭 추가"]',
      advanceImmediately: true,
      section: '[활동지] 실습',
      sectionMascot: '개체와 페이지를 직접 추가해 보는 구간이에요.',
    },
    {
      id: 'ws-force-add-page',
      mascot: '아래 「페이지 추가」를 눌러 새 페이지를 만들어 보세요.',
      targetSelector: '[data-tutorial-ws="add-page"], [aria-label="페이지 추가"]',
      advanceImmediately: true,
    },
    {
      id: 'ws-practice',
      mascot:
        '활동지를 자유롭게 구성해 보세요. 닫기·저장은 잠겨 있어요. 충분히 살펴본 뒤 "다음"을 눌러 주세요(창이 닫힙니다).',
      targetSelector: '[data-tutorial-ws="editor-modal"]',
      advanceWhenSelectorGone: '[data-tutorial-ws="editor-modal"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      blockClickSelectors: '[data-tutorial-ws="cancel"], [data-tutorial-ws="save"]',
    },
    {
      id: 'outro',
      mascot:
        '수업 활동지 튜토리얼을 마쳤어요! 수업 중에도 연간진도·프로그램 표의 「활동지」로 바로 열어 쓸 수 있어요.',
      actionLabel: '완료',
      presentation: 'celebration',
      cardTitle: '잘하셨어요!',
      placement: 'center',
    },
  ],
};

const classWorksheet: TutorialDefinition = {
  ...classWorksheetRaw,
  steps: classWorksheetRaw.steps.map((step) => {
    const recover = RECOVER_BY_STEP_ID[step.id];
    if (!recover && !step.targetMissingHint) return step;
    return {
      ...step,
      ...(recover ? { recover } : {}),
      targetMissingHint: buildTutorialTargetMissingHint(recover, step.targetMissingHint),
    };
  }),
};

export default classWorksheet;
